import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  encode as encodeCl100k,
  decode as decodeCl100k,
} from "gpt-tokenizer/encoding/cl100k_base";
import {
  encode as encodeO200k,
  decode as decodeO200k,
} from "gpt-tokenizer/encoding/o200k_base";
import { Copy, Check, Trash2, ArrowLeft } from "lucide-react";

// ── Model definitions with pricing (per 1M tokens) ──────────
// Pricing per 1M tokens — verified Feb 2026
const MODELS = [
  {
    id: "claude-opus-4.6",
    name: "Claude Opus 4.6",
    provider: "Anthropic",
    encoding: "cl100k_base" as const, // approximation — Anthropic uses proprietary tokenizer
    inputPrice: 5,
    outputPrice: 25,
  },
  {
    id: "claude-sonnet-4.5",
    name: "Claude Sonnet 4.5",
    provider: "Anthropic",
    encoding: "cl100k_base" as const,
    inputPrice: 3,
    outputPrice: 15,
  },
  {
    id: "claude-haiku-4.5",
    name: "Claude Haiku 4.5",
    provider: "Anthropic",
    encoding: "cl100k_base" as const,
    inputPrice: 1,
    outputPrice: 5,
  },
  {
    id: "gpt-5.2",
    name: "GPT-5.2",
    provider: "OpenAI",
    encoding: "o200k_base" as const, // approximation — GPT-5 tokenizer not public
    inputPrice: 1.75,
    outputPrice: 14,
  },
  {
    id: "gpt-5.2-pro",
    name: "GPT-5.2 Pro",
    provider: "OpenAI",
    encoding: "o200k_base" as const,
    inputPrice: 21,
    outputPrice: 168,
  },
  {
    id: "gpt-5-mini",
    name: "GPT-5 mini",
    provider: "OpenAI",
    encoding: "o200k_base" as const,
    inputPrice: 0.25,
    outputPrice: 2,
  },
  {
    id: "gpt-4.1",
    name: "GPT-4.1",
    provider: "OpenAI",
    encoding: "o200k_base" as const,
    inputPrice: 3,
    outputPrice: 12,
  },
  {
    id: "gpt-4.1-mini",
    name: "GPT-4.1 mini",
    provider: "OpenAI",
    encoding: "o200k_base" as const,
    inputPrice: 0.8,
    outputPrice: 3.2,
  },
  {
    id: "gpt-4.1-nano",
    name: "GPT-4.1 nano",
    provider: "OpenAI",
    encoding: "o200k_base" as const,
    inputPrice: 0.2,
    outputPrice: 0.8,
  },
] as const;

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function formatCost(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
}

// ── Token Counter Page ───────────────────────────────────────
export function TokenCounter() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  // Update page title
  useEffect(() => {
    document.title = "LLM Token Counter — Ballah Code";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Count tokens for GPT-4o, Claude, Gemini and more. Free, instant, runs in your browser. See token counts and cost estimates for every major LLM.",
      );
    }
  }, []);

  // Compute all stats
  const stats = useMemo(() => {
    const chars = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const lines = text ? text.split("\n").length : 0;
    const sentences = text.trim()
      ? text.split(/[.!?]+/).filter((s) => s.trim().length > 0).length
      : 0;

    // Tokenize with each encoding
    let cl100kTokens = 0;
    let o200kTokens = 0;

    if (text) {
      try {
        cl100kTokens = encodeCl100k(text).length;
      } catch {
        cl100kTokens = Math.ceil(chars / 4); // fallback
      }
      try {
        o200kTokens = encodeO200k(text).length;
      } catch {
        o200kTokens = Math.ceil(chars / 4);
      }
    }

    return { chars, words, lines, sentences, cl100kTokens, o200kTokens };
  }, [text]);

  const getTokenCount = useCallback(
    (encoding: "cl100k_base" | "o200k_base") => {
      return encoding === "o200k_base" ? stats.o200kTokens : stats.cl100kTokens;
    },
    [stats],
  );

  const handleClear = () => setText("");
  const handleCopy = async () => {
    const summary = MODELS.map(
      (m) => `${m.name}: ${formatNumber(getTokenCount(m.encoding))} tokens`,
    ).join("\n");
    await navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-[18px]">
        <Link
          to="/"
          className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
        >
          ballah code
        </Link>
        <div className="flex items-center gap-6 text-[13px] text-zinc-600">
          <Link to="/tools" className="hover:text-zinc-400 transition-colors">
            tools
          </Link>
          <Link to="/" className="hover:text-zinc-400 transition-colors">
            home
          </Link>
        </div>
      </nav>

      <main className="max-w-[1060px] mx-auto px-8 pt-12 pb-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
          <Link to="/tools" className="hover:text-zinc-400 transition-colors">
            Tools
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-400">LLM Token Counter</span>
        </div>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
            LLM Token Counter
          </h1>
          <p className="text-zinc-500 text-[15px] leading-relaxed max-w-xl">
            Paste text to instantly count tokens for every major LLM. See cost
            estimates for GPT-4o, Claude, Gemini and more. Runs entirely in your
            browser — nothing is sent to a server.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
          {/* Input area */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label className="text-[13px] text-zinc-500 font-medium">
                Input text
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  disabled={!text}
                  className="text-[12px] text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {copied ? (
                    <>
                      <Check size={12} /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Copy counts
                    </>
                  )}
                </button>
                <button
                  onClick={handleClear}
                  disabled={!text}
                  className="text-[12px] text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Trash2 size={12} /> Clear
                </button>
              </div>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste or type your text here..."
              spellCheck={false}
              className="w-full h-[420px] p-4 bg-[#111113] border border-white/[0.06] rounded-xl text-[14px] font-mono text-zinc-300 leading-relaxed resize-none outline-none placeholder:text-zinc-700 focus:border-white/[0.12] transition-colors"
            />

            {/* Quick stats bar */}
            <div className="flex items-center gap-6 text-[12px] text-zinc-600 px-1">
              <span>
                <span className="text-zinc-400">
                  {formatNumber(stats.chars)}
                </span>{" "}
                characters
              </span>
              <span>
                <span className="text-zinc-400">
                  {formatNumber(stats.words)}
                </span>{" "}
                words
              </span>
              <span>
                <span className="text-zinc-400">
                  {formatNumber(stats.lines)}
                </span>{" "}
                lines
              </span>
              <span>
                <span className="text-zinc-400">
                  {formatNumber(stats.sentences)}
                </span>{" "}
                sentences
              </span>
            </div>
          </div>

          {/* Results panel */}
          <div className="flex flex-col gap-3">
            <div className="text-[13px] text-zinc-500 font-medium">
              Token counts & cost estimates
            </div>

            <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_90px_90px] gap-px px-4 py-2.5 border-b border-white/[0.06] text-[11px] text-zinc-600 uppercase tracking-wider font-medium">
                <span>Model</span>
                <span className="text-right">Tokens</span>
                <span className="text-right">Input cost</span>
              </div>

              {/* Model rows */}
              {MODELS.map((model, i) => {
                const tokens = getTokenCount(model.encoding);
                const cost = (tokens / 1_000_000) * model.inputPrice;
                const isLast = i === MODELS.length - 1;

                return (
                  <div
                    key={model.id}
                    className={`grid grid-cols-[1fr_90px_90px] gap-px px-4 py-3 ${!isLast ? "border-b border-white/[0.04]" : ""
                      } hover:bg-white/[0.02] transition-colors`}
                  >
                    <div>
                      <span className="text-[13px] text-zinc-300">
                        {model.name}
                      </span>
                      <span className="text-[11px] text-zinc-700 ml-2">
                        {model.provider}
                      </span>
                    </div>
                    <span className="text-[13px] text-zinc-300 text-right font-mono">
                      {formatNumber(tokens)}
                    </span>
                    <span className="text-[13px] text-zinc-500 text-right font-mono">
                      {formatCost(cost)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Pricing note */}
            <p className="text-[11px] text-zinc-700 px-1 leading-relaxed">
              Costs shown are input token pricing. Claude token counts are
              approximate — Anthropic uses a proprietary tokenizer. Prices
              verified Feb 2026.
            </p>

            {/* Encoding breakdown */}
            <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-4 mt-1">
              <div className="text-[12px] text-zinc-500 font-medium mb-3">
                Encoding breakdown
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-zinc-500 font-mono">cl100k_base</span>
                  <span className="text-zinc-300 font-mono">
                    {formatNumber(stats.cl100kTokens)}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-700">
                  Used by Claude (approx). Based on OpenAI's ~100k vocabulary
                  tokenizer.
                </p>
                <div className="flex items-center justify-between text-[13px] mt-3">
                  <span className="text-zinc-500 font-mono">o200k_base</span>
                  <span className="text-zinc-300 font-mono">
                    {formatNumber(stats.o200kTokens)}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-700">
                  Used by GPT-5.2, GPT-5 mini, GPT-4.1 family. ~200k vocabulary.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SEO content */}
        <section className="mt-20 max-w-2xl">
          <h2 className="text-xl font-medium tracking-[-0.02em] mb-4 text-zinc-300">
            What are LLM tokens?
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-500 leading-[1.75]">
            <p>
              Large language models don't read text the way humans do. They
              break text into <strong className="text-zinc-400">tokens</strong>{" "}
              — chunks that roughly correspond to words, word pieces, or
              individual characters. The word "tokenization" itself might be
              split into ["token", "ization"] — that's 2 tokens.
            </p>
            <p>
              Token counts matter because they directly determine{" "}
              <strong className="text-zinc-400">cost</strong> and{" "}
              <strong className="text-zinc-400">context window usage</strong>.
              Every API call is billed per token, and every model has a maximum
              context window (e.g. 128k tokens for GPT-4o, 200k for Claude).
            </p>
            <p>
              Different models use different tokenizers. OpenAI's GPT-5 and
              GPT-4.1 families use the o200k_base encoding (~200k vocabulary).
              Anthropic and Google use proprietary tokenizers. The same text can
              produce different token counts depending on the model.
            </p>
          </div>

          <h2 className="text-xl font-medium tracking-[-0.02em] mb-4 mt-12 text-zinc-300">
            How accurate is this counter?
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-500 leading-[1.75]">
            <p>
              For OpenAI models, counts use the o200k_base tokenizer which is{" "}
              <strong className="text-zinc-400">very close</strong> to what
              OpenAI uses internally for GPT-5 and GPT-4.1 models.
            </p>
            <p>
              For Claude models, the counts are{" "}
              <strong className="text-zinc-400">approximate</strong>. Anthropic
              uses a proprietary tokenizer that isn't publicly available as a
              JavaScript library. We use cl100k_base as a reasonable
              approximation — in practice, counts are usually within 5-10% of
              the actual value.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1060px] mx-auto text-xs text-zinc-800">
        <Link to="/" className="hover:text-zinc-600 transition-colors">
          ballah code
        </Link>
        <span>ballah tech</span>
      </footer>
    </div>
  );
}
